/**
 * Types mirroring the FastAPI contract.
 *
 * Money is ALWAYS a string ("115.00"), never a number. JavaScript numbers
 * are IEEE-754 doubles and cannot represent 0.1 exactly, so parsing money
 * into a number would reintroduce the rounding drift the backend works
 * hard to avoid. Format for display, compute with care, send back strings.
 */

export type DrCr = 'D' | 'C'
export type Money = string

export type VoucherKind =
  | 'CONTRA' | 'PAYMENT' | 'RECEIPT' | 'JOURNAL'
  | 'SALES' | 'PURCHASE' | 'CREDIT_NOTE' | 'DEBIT_NOTE'

export type BillRefType = 'NEW' | 'AGAINST' | 'ADVANCE' | 'ON_ACCOUNT'

export type AccountRole =
  | 'CASH' | 'BANK' | 'SUNDRY_DEBTORS' | 'SUNDRY_CREDITORS'
  | 'SALES' | 'SALES_RETURN' | 'PURCHASES' | 'PURCHASE_RETURN'
  | 'VAT_OUTPUT' | 'VAT_INPUT' | 'STOCK' | 'ROUNDING'
  | 'OPENING_BALANCE' | 'RETAINED_EARNINGS'

// --- auth -------------------------------------------------------------

export interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

export interface Company {
  id: number
  code: string
  name_en: string
  name_ar?: string | null
  vat_number?: string | null
  base_currency: string
  trip_ref_column?: TripRefColumn
  /** Switched-on modules ('transport'); the menu hides the rest. */
  modules?: string[]
}

export interface Me {
  id: number
  username: string
  full_name: string | null
  role: string
  permissions: string[]
  tenant_id: string
  company_id: number
  fin_year: number
  companies: Company[]
  /** True when this user sees only vouchers they entered themselves. */
  restrict_to_own: boolean
  /** Cash/bank ledger codes they may transact on. Empty = unrestricted. */
  allowed_cash_bank: string[]
  /** Modules this installation may use (set by the operator, not the
   *  company): the only ones the Modules page offers. */
  allowed_modules?: string[]
  /** Party ledger codes assigned to them. Empty = unrestricted. */
  allowed_parties: string[]
}

/** Permission strings, mirrored from the backend enum. */
export const P = {
  postReceipt: 'voucher:post:receipt',
  postPayment: 'voucher:post:payment',
  postContra: 'voucher:post:contra',
  postJournal: 'voucher:post:journal',
  postSales: 'voucher:post:sales',
  postPurchase: 'voucher:post:purchase',
  reverse: 'voucher:reverse',
  backdate: 'voucher:backdate',
  viewAll: 'voucher:view:all',
  masterView: 'master:view',
  masterEdit: 'master:edit',
  createParty: 'master:create_party',
  reportOperational: 'report:operational',
  reportFinancial: 'report:financial',
  reportExport: 'report:export',
  transportView: 'transport:view',
  transportEntry: 'transport:entry',
  transportInvoice: 'transport:invoice',
  transportMasters: 'transport:masters',
  transportCorrect: 'transport:correct',
  inventoryView: 'inventory:view',
  inventoryMasters: 'inventory:masters',
  priceOverride: 'inventory:price_override',
  inventoryTransfer: 'inventory:transfer',
  inventoryAdjust: 'inventory:adjust',
  inventoryVan: 'inventory:van',
  adminUsers: 'admin:users',
  adminPeriods: 'admin:periods',
  adminCompany: 'admin:company',
} as const

// --- masters ----------------------------------------------------------

export interface Ledger {
  id: number
  code: string
  name_en: string
  name_ar?: string | null
  /** Display code only. CHANGES when the company switches numbering
   *  style, so never match against it. */
  group_code: string
  /** Stable identity of the ledger's OWN group ('JEDDAH_REGION'),
   *  whatever the display style. Correct for showing where an account
   *  sits; wrong for deciding whether it is a customer. */
  group_key: string
  /** The stable keys of every group above it, root first, ending in
   *  group_key. A restricted picker matches against this: once the chart
   *  is more than one level deep, a customer under 'Jeddah region' has
   *  group_key 'JEDDAH_REGION' and no longer equals 'DEBTORS'. */
  group_keys?: string[]
  nature: string
  maintain_bill_wise: boolean
  is_active: boolean
}

export interface AccountGroupNode {
  id: number
  code: string
  system_key?: string | null
  name_en: string
  name_ar: string | null
  nature: string
  affects_gross_profit: boolean
  children: AccountGroupNode[]
  ledgers: { id: number; code: string; name_en: string; name_ar: string | null }[]
}

/** One invoice in a sales or purchase register. */
export interface RegisterRow {
  voucher_id: number
  voucher_no: string
  date: string
  status: string
  party_code: string | null
  party_name: string | null
  party_tax_number: string | null
  narration: string | null
  taxable: Money
  vat: Money
  total: Money
}

export interface Register {
  kind: string
  from_date: string
  to_date: string
  count: number
  taxable: Money
  vat: Money
  total: Money
  rows: RegisterRow[]
}

/** Totals the trip register prints, computed server-side over the same
 *  rows the screen shows -- including after per-user scoping. */
export interface TripTotals {
  revenue: Money
  subcontract_cost: Money
  gross: Money
  /** Total kilograms over the same rows. Older servers omit it. */
  weight_kg?: string
  billed: number
  unbilled: number
}

export interface PartyCreate {
  code: string
  name_en: string
  name_ar?: string
  kind: 'customer' | 'supplier'
  credit_days?: number
  tax_number?: string
}

// --- vouchers ---------------------------------------------------------

export interface BillAllocationIn {
  ref_type: BillRefType
  ref_name: string
  amount: Money
  due_date?: string | null
}

export interface VoucherLineIn {
  dr_cr: DrCr
  amount: Money
  ledger_code?: string
  role?: AccountRole
  narration?: string
  bill_allocations?: BillAllocationIn[]
}

export interface VoucherCreate {
  voucher_kind: VoucherKind
  voucher_date: string
  lines: VoucherLineIn[]
  narration?: string
  source_system?: string
  source_doc_type?: string
  source_doc_id?: string
}

export interface VoucherLineOut {
  line_no: number
  ledger_id: number
  ledger_code: string
  ledger_name: string
  dr_cr: DrCr
  amount: Money
  narration: string | null
}

export interface Voucher {
  id: number
  uuid: string
  voucher_no: string
  voucher_kind: string
  voucher_date: string
  narration: string | null
  status: string
  total: Money
  lines: VoucherLineOut[]
  reverses_voucher_id: number | null
  reversed_by_voucher_id: number | null
}

// --- reports ----------------------------------------------------------

export interface TrialBalanceRow {
  code: string
  name_en: string
  name_ar: string | null
  nature: string
  opening: Money
  debit: Money
  credit: Money
  closing_dr: Money
  closing_cr: Money
}

export interface TrialBalance {
  from_date: string
  to_date: string
  rows: TrialBalanceRow[]
  total_debit: Money
  total_credit: Money
  is_balanced: boolean
}

export interface DayBookLine {
  ledger_id: number
  code: string
  name_en: string
  dr_cr: DrCr
  amount: Money
}

export interface DayBookEntry {
  voucher_id: number
  voucher_no: string
  voucher_kind: string
  date: string
  narration: string | null
  status: string
  amount: Money
  lines: DayBookLine[]
}

export interface DayBook {
  from_date: string
  to_date: string
  count: number
  entries: DayBookEntry[]
}

export interface LedgerStatementRow {
  date: string
  voucher_id: number
  voucher_no: string
  voucher_kind: string
  particulars: string
  narration: string | null
  debit: Money
  credit: Money
  running: Money
}

export interface LedgerStatement {
  code: string
  name_en: string
  from_date: string
  to_date: string
  opening: Money
  closing: Money
  total_debit: Money
  total_credit: Money
  rows: LedgerStatementRow[]
}


// --- chart of accounts admin ------------------------------------------

export type GroupStyle = 'MNEMONIC' | 'DOTTED' | 'NUMERIC'

export interface CodePolicy {
  group_style: GroupStyle
  ledger_serial_width: number
  allow_manual_codes: boolean
}

export interface AccountGroupFlat {
  id: number
  code: string
  system_key: string | null
  name_en: string
  name_ar: string | null
  nature: string
  parent_id: number | null
  affects_gross_profit: boolean
  /** Part of the standard chart: renameable, never deletable. */
  is_system: boolean
  /** Accounts sitting directly in this group, and groups under it. The
   *  screen uses both to say why a group cannot be deleted. */
  ledger_count: number
  child_count: number
}

export interface LedgerFull {
  id: number
  code: string
  name_en: string
  name_ar: string | null
  group_code: string
  group_name: string
  nature: string
  opening_balance: Money
  opening_dr_cr: DrCr
  maintain_bill_wise: boolean
  credit_days: number | null
  tax_number: string | null
  /** Party contact details. All optional: an account created mid-voucher
   *  carries a name and nothing else. */
  cr_number: string | null
  address: string | null
  city: string | null
  phone: string | null
  email: string | null
  contact_person: string | null
  is_active: boolean
  is_system: boolean
}

export interface RenumberChange { id: number; name: string; old: string; new: string }
export interface RenumberResult {
  dry_run: boolean; count: number; changes: RenumberChange[]
}

export interface ImportResult {
  dry_run: boolean
  created: number
  rejected: number
  rows: { row: number; code: string; name: string }[]
  errors: { row: number; code?: string; reason: string }[]
}

// --- users -------------------------------------------------------------

export interface StaffUser {
  id: number
  username: string
  full_name: string | null
  role: string
  is_active: boolean
  restrict_to_own: boolean
  permissions: string[]
  allowed_cash_bank: string[]
  allowed_parties: string[]
  /** Companies this user may open -- only those the viewing admin can
   *  also open, so a company the admin cannot see is never revealed. */
  company_ids: number[]
}

export interface RoleTemplate { role: string; permissions: string[] }

/** One row of the Companies screen. */
export interface CompanyRow {
  id: number
  code: string
  name_en: string
  name_ar: string | null
  vat_number: string | null
  cr_number: string | null
  user_count: number
  /** The company this session is in. */
  is_current: boolean
  year_code: number | null
  year_start: string | null
  year_end: string | null
}

export interface CompanyCreate {
  name_en: string
  start_date: string
  name_ar?: string
  code?: string
  vat_number?: string
  cr_number?: string
}

// --- company -----------------------------------------------------------

export interface CompanyInfo {
  id: number
  code: string
  name_en: string
  name_ar: string | null
  /** Registered name for tax invoices, when the books use another. */
  legal_name: string | null
  vat_number: string | null
  cr_number: string | null
  address_en: string | null
  address_ar: string | null
  phone: string | null
  email: string | null
  /** Data URL, shown on the printed letterhead. */
  logo: string | null
  base_currency: string
  bank_name: string | null
  bank_account: string | null
  bank_iban: string | null
  /** The fifth column on trips and the tax invoice. */
  trip_ref_column: TripRefColumn
  invoice_prefix: string
  invoice_next_no: number
  /** e.g. "INV/2026/00286" -- what the next invoice will be numbered. */
  invoice_next_label: string
  modules?: string[]
  /** Inventory: may a sale take a store below zero? */
  allow_negative_stock?: boolean
}

export type TripRefColumn = 'WAY_BILL' | 'WEIGHT'

export interface FinancialYear {
  id: number; year_code: number; start_date: string; end_date: string
  is_locked: boolean; is_current: boolean; voucher_count: number
}


// --- outstanding & ageing ---------------------------------------------

export const BUCKETS = ['current', 'd1_30', 'd31_60', 'd61_90', 'd90_plus'] as const
export type Bucket = typeof BUCKETS[number]

export interface OpenBill {
  ref_name: string
  bill_date: string
  due_date: string | null
  original: Money
  settled: Money
  outstanding: Money
  days_overdue: number
  bucket: Bucket
  voucher_id: number | null
  voucher_no: string | null
  is_advance: boolean
}

export interface PartyOutstanding {
  ledger_code: string
  ledger_name: string
  ledger_name_ar: string | null
  credit_days: number | null
  total: Money
  overdue: Money
  on_account: Money
  buckets: Record<Bucket, Money>
  bills: OpenBill[]
}

export interface OutstandingReport {
  as_of: string
  kind: 'receivable' | 'payable'
  buckets: Bucket[]
  totals: Record<Bucket, Money>
  grand_total: Money
  parties: PartyOutstanding[]
}

export interface PartyBills {
  ledger_code: string
  as_of: string
  balance: Money
  bills: OpenBill[]
}


// --- opening balances --------------------------------------------------

export interface OpeningAccount {
  ledger_code: string
  ledger_name: string
  ledger_name_ar: string | null
  group_code: string
  nature: string
  natural_side: DrCr
  amount: Money
  dr_cr: DrCr
}

export interface OpeningState {
  exists: boolean
  voucher_id: number | null
  voucher_no: string | null
  as_of: string
  fy_start: string
  total_debit: Money
  total_credit: Money
  difference: Money
  is_balanced: boolean
  accounts: OpeningAccount[]
}

export interface LegacyImportResult {
  dry_run: boolean
  converted: number
  difference: Money
  voucher_no: string | null
}


// --- transport ---------------------------------------------------------

export type VehicleKind = 'OWN' | 'SUBCONTRACTED'
export type TripStatus = 'DRAFT' | 'CONFIRMED' | 'INVOICED' | 'CANCELLED'

export interface Vehicle {
  id: number
  plate_no: string
  description: string | null
  kind: VehicleKind
  is_active: boolean
  subcontractor_code: string | null
  /** The expense group holding this truck's own costs. */
  cost_group_code?: string | null
  cost_group_name?: string | null
}

export interface Driver {
  id: number
  name: string
  phone: string | null
  is_active: boolean
}

export interface TransportRoute {
  id: number
  from_place: string
  to_place: string
  default_rate: Money | null
  income_ledger_code: string | null
  is_active: boolean
}

export interface Trip {
  id: number
  trip_no: string
  trip_date: string
  customer_code: string | null
  customer_name: string | null
  vehicle_plate: string | null
  vehicle_kind: VehicleKind | null
  driver_name: string | null
  route_id: number | null
  from_place: string
  to_place: string
  container_no: string | null
  do_number: string | null
  way_bill: string | null
  /** Kilograms, two decimals, as a string. */
  weight_kg: string | null
  rate: Money
  vat_rate: Money
  subcontract_cost: Money | null
  remarks: string | null
  status: TripStatus
  invoice_id: number | null
  invoice_no: string | null
}

/** One row of the printed invoice, in the client's own column order. */
export interface TripInvoiceLine {
  trip_id: number
  trip_no: string
  trip_date: string
  vehicle: string
  do_number: string | null
  way_bill: string | null
  weight_kg?: string | null
  container_no: string | null
  from_place: string
  to_place: string
  rate: Money
  vat: Money
  total: Money
  remarks: string | null
  /** On an issued invoice: what the trip says now (the line keeps the
   *  issued money). Present on the invoice detail only. */
  current_rate?: Money
  current_vat_rate?: Money
  driver_name?: string | null
  customer_code?: string | null
  /** False once the trip was moved to another customer. */
  on_invoice?: boolean
}

export interface InvoiceNote {
  id: number
  kind: 'CREDIT' | 'DEBIT'
  note_no: string
  note_date: string
  reason: string
  subtotal: Money
  vat_amount: Money
  total: Money
  voucher_id: number | null
  trip_count: number
}

/** One trip in a correction: only the keys present are changed. */
export interface TripCorrection {
  trip_id: number
  trip_date?: string
  vehicle_plate?: string
  driver_name?: string | null
  from_place?: string
  to_place?: string
  do_number?: string | null
  way_bill?: string | null
  weight_kg?: string | null
  remarks?: string | null
  rate?: string
  vat_rate?: string
  customer_code?: string
}

export interface TripInvoice {
  id: number
  invoice_no: string
  invoice_date: string
  due_date: string | null
  customer_code: string | null
  customer_name: string | null
  from_date: string | null
  to_date: string | null
  subtotal: Money
  vat_amount: Money
  total: Money
  voucher_id: number | null
  status: 'POSTED' | 'CANCELLED'
  trip_count: number
  lines?: TripInvoiceLine[]
  notes?: InvoiceNote[]
  /** Only on a correction's response. */
  new_notes?: InvoiceNote[]
  changes?: number
}

export interface InvoicePreview {
  customer_code: string
  customer_name: string
  lines: TripInvoiceLine[]
  subtotal: Money
  vat_amount: Money
  total: Money
}

/** What the grid sends. Money is a STRING all the way to the server. */
export interface TripIn {
  trip_date: string
  customer_code: string
  vehicle_plate: string
  rate: string
  from_place?: string | null
  to_place?: string | null
  route_id?: number | null
  driver_name?: string | null
  container_no?: string | null
  do_number?: string | null
  way_bill?: string | null
  weight_kg?: string | null
  vat_rate?: string
  subcontract_cost?: string | null
  remarks?: string | null
  status?: TripStatus
}


// --- cash book / bank book ------------------------------------------------

export interface CashBookRow {
  voucher_id: number
  voucher_no: string
  voucher_kind: string
  status: string
  account_code: string
  account_name: string
  particulars: string
  narration: string | null
  reference: string | null
  receipt: Money
  payment: Money
  balance: Money
}

export interface CashBook {
  kind: 'CASH' | 'BANK'
  from_date: string
  to_date: string
  opening: Money
  receipts: Money
  payments: Money
  closing: Money
  accounts: { code: string; name_en: string; opening: Money; receipts: Money
              payments: Money; closing: Money }[]
  days: { date: string; receipts: Money; payments: Money; closing: Money
          rows: CashBookRow[] }[]
}

// --- VAT return -----------------------------------------------------------

export interface VatDoc {
  voucher_id: number
  voucher_no: string
  voucher_kind: string
  date: string
  party_name: string | null
  party_tax_number: string | null
  amount: Money
  vat: Money
}

export interface VatBox {
  no: string
  key: string
  amount: Money
  adjustment: Money
  vat: Money
  docs: VatDoc[]
}

export interface VatReturn {
  from_date: string
  to_date: string
  sales: VatBox[]
  purchases: VatBox[]
  total_sales: VatBox
  total_purchases: VatBox
  vat_due: Money
}

// --- vehicle profitability --------------------------------------------------

export interface VehicleProfit {
  id: number
  plate_no: string
  kind: VehicleKind
  is_active: boolean
  cost_group_code: string | null
  cost_group_name: string | null
  trips: number
  revenue: Money
  hire_cost: Money
  cost: Money
  profit: Money
  margin: string | null
  cost_per_trip: Money | null
  fuel: Money
  fuel_per_trip: Money | null
  ledgers: { code: string; name: string; amount: Money }[]
  monthly: { month: string; trips: number; revenue: Money; cost: Money
             profit: Money }[]
}

export interface Profitability {
  from_date: string
  to_date: string
  months: string[]
  vehicles: VehicleProfit[]
  totals: { trips: number; revenue: Money; cost: Money; profit: Money }
  other_costs: Money
  unlinked_vehicles: string[]
}

// --- audit log --------------------------------------------------------------

export interface AuditEntry {
  id: number
  at: string
  actor: string | null
  action: string
  entity_type: string | null
  entity_id: string | null
  summary: string | null
  detail: Record<string, unknown> | null
  ip_address: string | null
}

// --- balances for the voucher picker ----------------------------------

/** One account's balance today: a positive figure and its side. */
export interface LedgerBalance {
  code: string
  name_en?: string
  name_ar?: string | null
  balance: Money
  side: DrCr
  /** Under the Cash or Bank group, at any depth. */
  cash_bank?: boolean
}

export interface LedgerBalances {
  as_of: string
  /** Non-zero balances only; a code that is absent is 0.00. */
  balances: LedgerBalance[]
}

// --- inventory (5.1) -----------------------------------------------------

export type VatCategory = 'STANDARD' | 'ZERO' | 'EXEMPT'

export interface InvUnit {
  uid?: string
  /** CTN, PK6 ... always upper case. */
  unit: string
  /** Base units in one of these: '24'. */
  factor: string
  barcode?: string | null
  /** Blank: factor x the item's base price. */
  sale_price?: Money | null
}

export interface InvItem {
  id: number
  uid: string
  code: string
  name_en: string
  name_ar?: string | null
  group_id?: number | null
  group_name?: string | null
  base_unit: string
  barcode?: string | null
  vat_category: VatCategory
  vat_rate: Money
  sale_price?: Money | null
  min_price?: Money | null
  /** Reorder below this, in base units, across all stores. */
  reorder_level?: string | null
  is_active: boolean
  units: InvUnit[]
  /** Quantity in base units per store id. */
  stock: Record<string, string>
  on_hand: string
  /** Only for users with financial reporting rights. */
  avg_cost?: string
  stock_value?: Money
  updated_at: string
}

export interface InvItemIn {
  code: string
  name_en: string
  name_ar?: string | null
  group_id?: number | null
  base_unit: string
  barcode?: string | null
  vat_category: VatCategory
  sale_price?: Money | null
  min_price?: Money | null
  reorder_level?: string | null
  is_active: boolean
  units: InvUnit[]
}

export interface InvGroup {
  id: number
  uid: string
  name_en: string
  name_ar?: string | null
  is_active: boolean
}

export type StoreKind = 'WAREHOUSE' | 'VAN'

export interface InvStore {
  id: number
  uid: string
  code: string
  name_en: string
  name_ar?: string | null
  kind: StoreKind
  salesman_user_id?: number | null
  /** A van's own series (V01 -> V01-INV/2026/00001); blank = the code. */
  doc_prefix?: string | null
  series_prefix?: string
  cash_ledger_code?: string | null
  cash_ledger_name?: string | null
  is_active: boolean
  item_count?: number
  stock_value?: Money
}

export interface InvSalesman { id: number; username: string; full_name?: string | null; role: string }

export interface OpeningStockLine {
  item_code: string
  item_name?: string
  item_name_ar?: string | null
  unit?: string | null
  qty: string
  unit_cost: string
  value?: Money
}

export interface OpeningStock {
  store_id: number
  lines: OpeningStockLine[]
  store_value: Money
  total_value?: Money
  books_value?: Money | null
  books_problem?: string
}

export interface InvImportResult {
  dry_run: boolean
  rows: number
  created?: number
  updated?: number
  ok?: boolean
  store_value?: Money
  lines?: number
  rejected: { row: number | null; code?: string; reason: string }[]
}

// --- purchases (5.2) -------------------------------------------------------

export interface PurchaseRow {
  id: number
  uid: string
  kind: 'INVOICE' | 'RETURN'
  doc_no: string
  doc_date: string
  due_date?: string | null
  supplier_code: string
  supplier_name: string
  supplier_name_ar?: string | null
  supplier_invoice_no?: string | null
  store_id: number
  store_name: string
  /** Set for a cash purchase: the account it was paid from. */
  paid_from?: string | null
  subtotal: Money
  vat_amount: Money
  total: Money
  status: 'POSTED' | 'CANCELLED'
  voucher_id?: number | null
  against_id?: number | null
  against_no?: string | null
  notes?: string | null
  entered_by?: string | null
}

export interface PurchaseLineOut {
  id: number
  line_no: number
  item_code: string
  item_name: string
  item_name_ar?: string | null
  unit: string
  factor: string
  qty: string
  unit_cost: string
  discount: Money
  net: Money
  vat_rate: Money
  vat_amount: Money
  total: Money
  returned: string
  returnable: string
}

export interface PurchaseDetail extends PurchaseRow {
  lines: PurchaseLineOut[]
  returns?: PurchaseRow[]
}

export interface PurchaseIn {
  supplier_code: string
  supplier_invoice_no?: string | null
  doc_date: string
  due_date?: string | null
  store_id: number
  cash_ledger_code?: string | null
  notes?: string | null
  lines: { item_code: string; qty: string; unit?: string | null; unit_cost: string
           discount?: string | null }[]
}

export interface LastCost {
  date: string
  doc_no: string
  supplier_code: string
  supplier_name: string
  unit: string
  unit_cost: string
  base_cost: string
}

// --- sales (5.3) -----------------------------------------------------------

export interface SaleRow {
  id: number
  uid: string
  kind: 'INVOICE' | 'RETURN'
  invoice_type: 'STANDARD' | 'SIMPLIFIED'
  doc_no: string
  doc_date: string
  due_date?: string | null
  customer_code?: string | null
  customer_name?: string | null
  customer_name_ar?: string | null
  customer_vat?: string | null
  store_id: number
  store_name: string
  /** Set when paid at the time: the cash or bank account. */
  paid_by?: string | null
  subtotal: Money
  vat_amount: Money
  total: Money
  /** Only for users with financial reporting rights. */
  cost?: Money
  gross_profit?: Money
  status: 'POSTED' | 'CANCELLED'
  voucher_id?: number | null
  against_id?: number | null
  against_no?: string | null
  notes?: string | null
  entered_by?: string | null
}

export interface SaleLineOut {
  id: number
  line_no: number
  item_code: string
  item_name: string
  item_name_ar?: string | null
  unit: string
  factor: string
  qty: string
  price: string
  discount: Money
  net: Money
  vat_rate: Money
  vat_amount: Money
  total: Money
  returned: string
  returnable: string
  price_override: boolean
  cost?: Money
}

export interface SaleDetail extends SaleRow {
  lines: SaleLineOut[]
  returns?: SaleRow[]
  qr_data_url?: string | null
  qr_missing_reason?: string | null
  seller: { name: string; name_ar?: string | null; vat_number?: string | null
            cr_number?: string | null; address?: string | null; address_ar?: string | null
            phone?: string | null }
  created_at?: string | null
  stock_warnings?: string[]
}

export interface SaleIn {
  customer_code?: string | null
  customer_name?: string | null
  customer_vat?: string | null
  doc_date: string
  due_date?: string | null
  store_id: number
  cash_ledger_code?: string | null
  notes?: string | null
  lines: { item_code: string; qty: string; unit?: string | null; price: string
           discount?: string | null }[]
}


// --- inventory 5.4: transfers, counts, stock reports -------------------

/** An item as a report row names it: enough to show "10 CTN + 4 PCS". */
export interface InvItemRef {
  item_id: number
  item_code: string
  item_name: string
  item_name_ar?: string | null
  base_unit: string
  pack_unit?: string | null
  pack_factor?: string | null
  group_name?: string | null
}

export interface TransferLine extends InvItemRef {
  id: number
  line_no: number
  unit: string
  factor: string
  qty: string
  base_qty: string
  unit_cost?: string
  value?: Money
}

export interface TransferRow {
  id: number
  uid: string
  doc_no: string
  doc_date: string
  from_store_id: number
  from_store_name: string
  from_store_kind: StoreKind
  to_store_id: number
  to_store_name: string
  to_store_kind: StoreKind
  status: 'POSTED' | 'CANCELLED'
  notes?: string | null
  entered_by?: string | null
  line_count: number
  created_at?: string | null
  /** Only with financial reporting rights. */
  value?: Money
}

export interface TransferDetail extends TransferRow { lines: TransferLine[] }

export interface TransferIn {
  doc_date: string
  from_store_id: number
  to_store_id: number
  notes?: string | null
  lines: { item_code: string; qty: string; unit?: string | null }[]
}

export type AdjustReason = 'COUNT' | 'DAMAGED' | 'EXPIRED'

export interface AdjustmentLine extends InvItemRef {
  id: number
  line_no: number
  book_qty: string
  counted_qty: string
  diff_qty: string
  unit_cost?: string
  value?: Money
}

export interface AdjustmentRow {
  id: number
  uid: string
  doc_no: string
  doc_date: string
  store_id: number
  store_name: string
  reason: AdjustReason
  status: 'POSTED' | 'CANCELLED'
  voucher_id?: number | null
  notes?: string | null
  entered_by?: string | null
  line_count: number
  short_lines: number
  excess_lines: number
  value?: Money
}

export interface AdjustmentDetail extends AdjustmentRow { lines: AdjustmentLine[] }

export interface AdjustmentIn {
  doc_date: string
  store_id: number
  reason: AdjustReason
  notes?: string | null
  lines: { item_code: string; counted: string; unit?: string | null }[]
}

export interface StockRow extends InvItemRef {
  qty: string
  per_store: Record<string, string>
  reorder_level?: string | null
  avg_cost?: string
  value?: Money
}

export interface StockReport {
  as_of: string
  store_id?: number | null
  store_name?: string | null
  rows: StockRow[]
  count: number
  total_value?: Money
  books_value?: Money | null
  difference?: Money | null
  /** Equal, or apart by no more than a halala per item of rounding. */
  agrees?: boolean | null
  stock_ledger?: { code: string; name: string } | null
}

export interface StockCardRow {
  date: string
  kind: string
  reference?: string | null
  doc_type?: 'sale' | 'purchase' | 'transfer' | 'adjustment' | null
  doc_id?: number | null
  particulars?: string | null
  store_name: string
  qty_in: string
  qty_out: string
  balance: string
  unit_cost?: string
}

export interface StockCard extends InvItemRef {
  store_id?: number | null
  store_name?: string | null
  from_date: string
  to_date: string
  opening: string
  closing: string
  total_in: string
  total_out: string
  rows: StockCardRow[]
}

export interface VanStock extends StockReport {
  code: string
  name: string
  name_ar?: string | null
  salesman_user_id?: number | null
  salesman?: string | null
}

export interface ItemSalesRow extends InvItemRef {
  qty: string
  sales: Money
  invoices: number
  cost?: Money
  profit?: Money
  margin?: string | null
}

export interface SalesByItem {
  from_date: string
  to_date: string
  rows: ItemSalesRow[]
  totals: { sales: Money; cost?: Money; profit?: Money; margin?: string | null }
}

export interface LowStockRow extends InvItemRef {
  qty: string
  reorder_level: string
  short: string
}

export interface InventorySummary {
  as_of: string
  low_stock: number
  sales_today?: Money
  profit_today?: Money
  stock_value?: Money
  books_value?: Money | null
  difference?: Money | null
  agrees?: boolean | null
}


// --- route sales (5.5) -------------------------------------------------

export interface VanPhone {
  id: number; store_id: number; store_name: string; store_code: string
  salesman?: string | null; device_name: string; created_at: string
  last_seen_at?: string | null; last_sync_at?: string | null
  revoked_at?: string | null; revoked_reason?: 'TAKEOVER' | 'REVOKED' | null
}

export interface VanSyncDoc {
  id: number; uid: string; kind: 'SALE' | 'RETURN' | 'RECEIPT'; doc_no: string
  doc_date: string; issued_at: string; total: Money; customer_code?: string | null
  store_id: number; store_name: string; device_name: string
  status: 'POSTED' | 'FAILED'; sale_id?: number | null; voucher_id?: number | null
  warnings: string[]; error?: string | null; attempts: number; received_at: string
}
